/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import TasksFilter from "../../components/Tasks/TasksFilter/TasksFilter";
import CustomDataGrid from "../../components/Shared/Table/CustomDataGrid";
import { useTaskDisplay } from "./hooks";
import AddScheduledTask from "../../components/Tasks/AddScheduledTasks";
import { useStoreDispatch } from "../../store/hooks";
import { setBasicDialogOpenFor } from "../../store/helpers/helperReducers";
import { useSelector } from "react-redux";
import { basicDialogSelector } from "../../store/helpers/helperSelectors";
import { DIALOGOPENFOR } from "../../globals/globals";
import { GridEventListener, GridRowParams } from "@mui/x-data-grid";
import TaskDrawer from "../../components/Drawers/TaskDrawer";
import { prepareScheduledTasksCellDataGrid } from "../Configuration/helpers";
import StandardTemplates from "./StandardTemplates";
import GoldenConfiguration from "./GoldenConfiguration";
import { setIsEditTask, setScheduledTask, setSelectedTask } from "../../store/api/tasks/reducer";
import { ITask, ITaskDetails } from "../../store/api/tasks/types";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Icon } from "../../components/Shared/Icon";
import { Loader2 } from "lucide-react";
import { useTasksFilter } from "../../components/Tasks/TasksFilter/hooks";
import { resetTaskFilters } from "../../store/filters/tasks/reducers";
import ActiveFilterChips from "../../components/Shared/Filter/ActiveFilterChips";
import ReportDisplay from "../Nodes/Reports/ReportDisplay";
import CustomAuditTemplates from "./custom-report/CustomAuditTemplates";

const Tasks: React.FC = () => {
  const dispatch = useStoreDispatch();
  const [selectedTab, setSelectedTab] = useState<String>("scheduledTab");
  const { permissions, data = [], isLoading, refetch, scheduledTasksColumns } = useTaskDisplay();

  const [rowClicked, setRowClicked] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<ITask>();
  const [isExporting, setIsExporting] = useState(false);
  const basicDialodOpenFor = useSelector(basicDialogSelector);
  const { filters: taskFilters } = useTasksFilter();

  const handleClearAllTaskFilters = useCallback(() => {
    dispatch(resetTaskFilters());
  }, [dispatch]);

  const rowsData = prepareScheduledTasksCellDataGrid(data) || [];

  const handleChange = (newTab: string) => {
    setSelectedTab(newTab);
  };

  const exportToCSV = () => {
    try {
      setIsExporting(true);

      const headers = ["Region", "Task", "Type", "Next Execution Time", "Last Completed", "Frequency", "Status", "Created By", "Description"];
      const csvRows = [
        headers.join(","),
        ...data.map((task: ITaskDetails) => {
          const row = [
            `"${task.geoRegion || ""}"`,
            `"${task.name || ""}"`,
            `"${task.taskName || ""}"`,
            task.nextTime && task.nextTime > 0 ? `"${dayjs(task.nextTime).format("DD-MMM-YY HH:mm:ss")}"` : '"None"',
            task.lastCompleted && task.lastCompleted > 0 ? `"${dayjs.utc(task.lastCompleted).local().format("DD-MMM-YY HH:mm:ss")}"` : '"None"',
            `"${task.period !== undefined && task.period !== null ? getPeriodDisplay(task.period) : ""}"`,
            `"${task.status || ""}"`,
            `"${task.createdBy || ""}"`,
            `"${(task.description || "").replace(/"/g, '""')}"`
          ];
          return row.join(",");
        })
      ];

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Tasks-List-${dayjs().format("DD-MMM-YY")}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting to CSV:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const getPeriodDisplay = (period: number): string => {
    if (!period || period === 0) return "Does Not Repeat";
    const hours = period / (1000 * 60 * 60);
    if (hours === 1) return "Hourly";
    if (hours === 24) return "Daily";
    if (hours === 168) return "Weekly";
    return `Every ${hours} hours`;
  };

  const addScheduledTaskHandler = () => {
    dispatch(setIsEditTask(false));
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.ADDSCHEDULEDTASK));
    dispatch(setScheduledTask({ nextTime: dayjs().add(1, "minute").valueOf() }));
  };

  const handleRowClicked: GridEventListener<"rowClick"> = (params: GridRowParams<ITask>) => {
    if (data.length > 0) {
      const taskDetails = data.filter((item: ITaskDetails) => item.objectKey === params?.row?.objectKey);
      dispatch(setSelectedTask(taskDetails[0]));
    }
    setRowClicked(true);
    setSelectedRowData(params.row);
  };

  const handleCloseButtonClicked = (value: boolean) => {
    setRowClicked(value);
  };

  const location = useLocation();

  useEffect(() => {
    const previousRoute = localStorage.getItem("currentRoute");
    if (previousRoute) {
      localStorage.setItem("previousRoute", previousRoute);
    }
    localStorage.setItem("currentRoute", location.pathname);
  }, [location]);

  return (
    <>
      {basicDialodOpenFor === DIALOGOPENFOR.ADDSCHEDULEDTASK && <AddScheduledTask />}
      {rowClicked && (
        <TaskDrawer data={selectedRowData} setRowClicked={setRowClicked} handleRefeshTasks={refetch} closedButtonClicked={handleCloseButtonClicked} />
      )}
      <div
        className="flex flex-col w-full overflow-y-auto px-4 md:px-8 mt-6 mb-2"
        style={{ color: "var(--foreground)" }}
      >
        <div
          className="bg-card text-card-foreground shadow"
          style={{
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius)",
            padding: "18px",
            boxShadow: "var(--shadow-card)",
            backgroundColor: "var(--background)",
          }}
        >
          <div className="flex flex-col">
            <div className="pl-1">
              <Tabs value={selectedTab as string} onValueChange={handleChange}>
                <div className="pr-1 mt-2">
                  <div style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <TabsList className="inline-flex h-auto gap-0 rounded-none border-0 bg-transparent p-0 text-muted-foreground shrink-0">
                      <TabsTrigger
                        value="scheduledTab"
                        className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-sm capitalize text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                      >
                        Scheduled Tasks
                      </TabsTrigger>
                      <TabsTrigger
                        value="Templates"
                        className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-sm capitalize text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                      >
                        Templates
                      </TabsTrigger>
                      <TabsTrigger
                        value="GoldenConfiguration"
                        className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-sm capitalize text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                      >
                        Golden Configuration
                      </TabsTrigger>
                      <TabsTrigger
                        value="CustomAuditTemplates"
                        className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-sm capitalize text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                      >
                        Custom Audit Templates
                      </TabsTrigger>
                      <TabsTrigger
                        value="Reports"
                        className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-sm capitalize text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                      >
                        Reports
                      </TabsTrigger>
                    </TabsList>
                  </div>
                </div>
              </Tabs>
            </div>
            <hr className="border-border" />
            {selectedTab === "scheduledTab" && (
              <div className="pt-2 px-1 pb-1 flex">
                <div className="w-full">
                  <TasksFilter />
                </div>
                {permissions?.hasOpCodeGroup("task_management") && (
                  <Button
                    className="h-10 px-4 text-sm font-medium border border-input bg-background shadow-sm flex items-center gap-2 hover:bg-muted hover:text-accent-foreground transition-colors"
                    variant="outline"
                    onClick={addScheduledTaskHandler}
                  >
                    <Icon name="add" size={18} />
                    Add Scheduled Task
                  </Button>
                )}
              </div>
            )}
            <div className="pt-2 px-1 pb-4">
              {selectedTab === "scheduledTab" ? (
                <>
                <div className="flex justify-between items-end mb-0 mt-4">
                <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-muted-foreground">{`Showing ${rowsData?.length || 0} results`}</span>
                      <ActiveFilterChips
                        filters={taskFilters}
                        onClearAll={handleClearAllTaskFilters}
                        dispatch={dispatch}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 shrink-0 relative -top-1"
                        disabled={isExporting}
                        onClick={exportToCSV}
                        aria-label="Export CSV"
                      >
                        {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon name="download" size={18} />}
                      </Button>
                    </div>
                  </div>
                  <CustomDataGrid
                    tableId="scheduledTask"
                    sx={{
                      minHeight: "410px",
                    }}
                    rows={rowsData}
                    columns={scheduledTasksColumns}
                    rowCount={rowsData?.length}
                    loading={isLoading}
                    disableColumnMenu={true}
                    getRowId={(row: ITask) => row.id}
                    onRowClick={handleRowClicked}
                    hideFooter={true}
                    sortingMode="client"
                    pagination={false}
                    fillContainer
                    allowVerticalScroll
                  />
                </>
              ) : selectedTab === "Templates" ? (
                <StandardTemplates />
              ) : selectedTab === "CustomAuditTemplates" ? (
                <CustomAuditTemplates />
              ) : selectedTab === "Reports" ? (
                <ReportDisplay />
              ) : (
                <GoldenConfiguration />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Tasks;
